import { useState } from 'react';
import { toast } from 'react-toastify';
import type { DevListingView, WishView } from '@dunea/shared';
import { apiClaimWish, apiListWishes } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CARD,
  LoadError,
  Pill,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { canClaimWish, wishStatusMeta } from '~/components/workbench/tools/marketplace/community-model';
import { kindLabel } from '~/components/workbench/tools/marketplace/marketplace-model';
import { apiErrorMessage } from '~/components/marketplace/developer-model';

/** The community wishlist, most wanted first: claim one you're building (optionally link a listing). */
export function WishClaimSection({ listings }: { listings: DevListingView[] }) {
  const wishes = useResource(() => apiListWishes({ sort: 'top', status: 'open' }), 'dev-wishes');
  const [showAll, setShowAll] = useState(false);
  const items = wishes.data ?? [];
  const shown = showAll ? items : items.slice(0, 5);

  return (
    <Section title="Wishlist" icon="i-ph:lightbulb">
      {wishes.error ? (
        <LoadError message="Could not load the wishlist." onRetry={() => void wishes.reload()} />
      ) : !wishes.data ? (
        <SkeletonCard lines={2} />
      ) : items.length === 0 ? (
        <div className={classNames(CARD, 'px-4 py-5 text-center text-[12px] text-muted')}>No open wishes.</div>
      ) : (
        <div className="space-y-2">
          {shown.map((w) => (
            <WishClaimRow
              key={w.id}
              wish={w}
              listings={listings.filter((l) => l.kind === w.kind)}
              onChanged={(next) => wishes.setData((prev) => (prev ?? []).map((x) => (x.id === next.id ? next : x)))}
            />
          ))}
          {items.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAll((s) => !s)}
              className="text-[11.5px] text-muted hover:text-primary"
            >
              {showAll ? 'Show fewer' : `Show all ${items.length}`}
            </button>
          )}
        </div>
      )}
    </Section>
  );
}

function WishClaimRow({
  wish,
  listings,
  onChanged,
}: {
  wish: WishView;
  listings: DevListingView[];
  onChanged: (next: WishView) => void;
}) {
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const status = wishStatusMeta(wish.status);

  const claim = async () => {
    setBusy(true);

    try {
      onChanged(await apiClaimWish(wish.id, slug || undefined));
      toast.success('Claimed. Voters see that you are building it.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not claim this wish.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classNames(CARD, 'space-y-2 p-3')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12.5px] text-primary">{wish.title}</div>
          <div className="text-[11px] text-muted">
            {kindLabel(wish.kind)} · {wish.voteCount} {wish.voteCount === 1 ? 'vote' : 'votes'}
            {wish.claimedBy && ` · claimed by ${wish.claimedBy.displayName}`}
          </div>
        </div>
        <Pill tone={status.tone} label={status.label} />
      </div>
      {canClaimWish(wish) && (
        <div className="flex gap-2">
          {listings.length > 0 && (
            <select
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={busy}
              aria-label="Link a listing"
              className="ui-input min-w-0 !py-1.5 !text-[11.5px]"
            >
              <option value="">No listing yet</option>
              {listings.map((l) => (
                <option key={l.slug} value={l.slug}>
                  {l.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => void claim()}
            disabled={busy}
            className={classNames(BTN_SECONDARY, 'shrink-0')}
          >
            {busy && <Spinner />}
            Claim
          </button>
        </div>
      )}
    </div>
  );
}
